import { useState } from 'react'
import { useAppStore } from '@/store/useAppStore'
import { useNavigate } from 'react-router-dom'
import { Modal } from '@/components/ui/Modal'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/utils'

const DELIVERABLE_FIELDS = [
  { key: 'reels',    label: 'Reels',    placeholder: '10' },
  { key: 'graphics', label: 'Graphics', placeholder: '20' },
  { key: 'photos',   label: 'Photos',   placeholder: '5' },
]

export function NewClientBoardModal({ parentId, open, onClose }) {
  const { createClientBoard, addSection, updateSection, setActiveProject } = useAppStore()
  const navigate = useNavigate()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [color, setColor] = useState('#e63946')
  const [icon, setIcon] = useState('◆')
  const [quotas, setQuotas] = useState({ reels: '', graphics: '', photos: '' })

  async function handleCreate() {
    if (!name.trim()) return
    const snap = { name: name.trim(), description, color, icon }
    const snapQuotas = { ...quotas }
    setName(''); setDescription(''); setColor('#e63946'); setIcon('◆'); setQuotas({ reels: '', graphics: '', photos: '' })
    onClose()
    const id = await createClientBoard(parentId, snap)
    if (!id) return
    // Section order: Graphics, Photos, Reels, Misc, Approval, Complete
    const graphicsId = addSection(id, 'Graphics')
    const photosId   = addSection(id, 'Photos')
    const reelsId    = addSection(id, 'Reels')
    addSection(id, 'Misc', 'misc')
    addSection(id, 'Approval', 'approval')
    addSection(id, 'Complete', 'complete')
    if (snapQuotas.graphics) updateSection(graphicsId, { deliverableType: 'graphics', deliverableQuota: Number(snapQuotas.graphics) })
    if (snapQuotas.photos)   updateSection(photosId,   { deliverableType: 'photos',   deliverableQuota: Number(snapQuotas.photos) })
    if (snapQuotas.reels)    updateSection(reelsId,    { deliverableType: 'reels',    deliverableQuota: Number(snapQuotas.reels) })
    setActiveProject(id)
    navigate(`/project/${id}`)
  }

  return (
    <Modal open={open} onClose={onClose} title="New client board" size="sm">
      <div className="space-y-4">
        {/* Preview */}
        <div className="flex items-center gap-3 p-3 bg-surface-raised rounded-xl border border-border">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center text-lg font-bold flex-shrink-0" style={{ background: color + '22', color }}>
            {icon}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-text truncate">{name || 'Client name'}</p>
            <p className="text-xs text-text-muted truncate">{description || 'No description'}</p>
          </div>
        </div>

        {/* Name */}
        <div>
          <label className="text-xs text-text-muted mb-1.5 block">Client name</label>
          <Input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Victoria Weddings"
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
          />
        </div>

        {/* Description */}
        <div>
          <label className="text-xs text-text-muted mb-1.5 block">Description</label>
          <Input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Package details, notes…"
          />
        </div>

        {/* Color */}
        <div>
          <label className="text-xs text-text-muted mb-2 block">Color</label>
          <div className="flex flex-wrap gap-2">
            {COLORS.map((c) => (
              <button
                key={c}
                onClick={() => setColor(c)}
                className={cn(
                  'w-6 h-6 rounded-full transition-all active:scale-[0.94]',
                  color === c ? 'ring-2 ring-offset-2 ring-offset-surface ring-white/50 scale-110' : 'hover:scale-110'
                )}
                style={{ background: c, border: c === '#ffffff' ? '1px solid #444' : undefined }}
              />
            ))}
          </div>
        </div>

        {/* Icon */}
        <div>
          <label className="text-xs text-text-muted mb-2 block">Icon</label>
          <div className="flex flex-wrap gap-2">
            {ICONS.map((ic) => (
              <button
                key={ic}
                onClick={() => setIcon(ic)}
                className={cn(
                  'w-8 h-8 rounded-lg flex items-center justify-center text-base transition-all active:scale-[0.94]',
                  icon === ic ? 'bg-accent/20 text-accent' : 'bg-surface-raised text-text-muted hover:text-text hover:bg-surface'
                )}
              >
                {ic}
              </button>
            ))}
          </div>
        </div>

        {/* Monthly deliverable quotas */}
        <div>
          <label className="text-xs text-text-muted mb-2 block">Monthly deliverables</label>
          <div className="grid grid-cols-3 gap-2">
            {DELIVERABLE_FIELDS.map((f) => (
              <div key={f.key}>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-text-faint block mb-1">{f.label}</label>
                <input
                  type="number"
                  min="0"
                  max="999"
                  value={quotas[f.key]}
                  onChange={(e) => setQuotas((q) => ({ ...q, [f.key]: e.target.value }))}
                  placeholder={f.placeholder}
                  className="w-full h-8 bg-surface-raised border border-border rounded-lg px-2.5 text-sm text-text placeholder:text-text-faint focus:outline-none focus:ring-1 focus:ring-accent/40"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-1">
          <Button variant="ghost" onClick={onClose} className="flex-1">Cancel</Button>
          <Button variant="primary" onClick={handleCreate} disabled={!name.trim()} className="flex-1">
            Create board
          </Button>
        </div>
      </div>
    </Modal>
  )
}

const COLORS = [
  '#e63946', '#f97316', '#eab308', '#22c55e',
  '#06b6d4', '#3b82f6', '#a78bfa', '#ec4899',
  '#6b7280', '#ffffff',
]

const ICONS = [
  // Shapes
  '◆', '◈', '✦', '◉', '★', '❋', '⬡', '▲', '●', '■',
  // People & faces
  '🎤', '🧑‍🎨', '👤', '🤝', '💼',
  // Creative & media
  '🎬', '📸', '🎨', '✏️', '🖼️', '📹', '🎵', '🎧', '📱', '💻',
  // Business & work
  '🏢', '📊', '📋', '📌', '🗂️', '📁', '🔖', '💡', '🚀', '⚡',
  // Nature & animals
  '🌿', '🌸', '🔥', '⭐', '🌙', '🌊', '🦋', '🐧', '🦖', '🐝',
  // Objects & symbols
  '💎', '🏆', '🎯', '🔑', '🧩', '🎁', '🛠️', '⚙️', '🌐', '📡',
]

export function NewProjectModal({ open, onClose }) {
  const { addProject, addSection, setActiveProject } = useAppStore()
  const navigate = useNavigate()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [color, setColor] = useState('#e63946')
  const [icon, setIcon] = useState('◆')
  const [privacy, setPrivacy] = useState('private')

  async function handleCreate() {
    if (!name.trim()) return
    const snap = { name: name.trim(), description, color, icon, privacy }
    setName(''); setDescription(''); setColor('#e63946'); setIcon('◆')
    onClose()
    const id = await addProject(snap)
    if (!id) return
    addSection(id, 'To Do')
    addSection(id, 'In Progress')
    addSection(id, 'Done')
    addSection(id, 'Approval', 'approval')
    addSection(id, 'Complete', 'complete')
    setActiveProject(id)
    navigate(`/project/${id}`)
  }

  return (
    <Modal open={open} onClose={onClose} title="New project" size="sm">
      <div className="space-y-4">
        {/* Preview */}
        <div className="flex items-center gap-3 p-3 bg-surface-raised rounded-xl border border-border">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center text-lg font-bold flex-shrink-0" style={{ background: color + '22', color }}>
            {icon}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-text truncate">{name || 'Project name'}</p>
            <p className="text-xs text-text-muted truncate">{description || 'No description'}</p>
          </div>
        </div>

        {/* Name */}
        <div>
          <label className="text-xs text-text-muted mb-1.5 block">Project name</label>
          <Input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Brand Campaign Q2"
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
          />
        </div>

        {/* Description */}
        <div>
          <label className="text-xs text-text-muted mb-1.5 block">Description</label>
          <Input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What's this project about?"
          />
        </div>

        {/* Color */}
        <div>
          <label className="text-xs text-text-muted mb-2 block">Color</label>
          <div className="flex flex-wrap gap-2">
            {COLORS.map((c) => (
              <button
                key={c}
                onClick={() => setColor(c)}
                className={cn(
                  'w-6 h-6 rounded-full transition-all active:scale-[0.94]',
                  color === c ? 'ring-2 ring-offset-2 ring-offset-surface ring-white/50 scale-110' : 'hover:scale-110'
                )}
                style={{ background: c, border: c === '#ffffff' ? '1px solid #444' : undefined }}
              />
            ))}
          </div>
        </div>

        {/* Icon */}
        <div>
          <label className="text-xs text-text-muted mb-2 block">Icon</label>
          <div className="flex flex-wrap gap-2">
            {ICONS.map((ic) => (
              <button
                key={ic}
                onClick={() => setIcon(ic)}
                className={cn(
                  'w-8 h-8 rounded-lg flex items-center justify-center text-base transition-all active:scale-[0.94]',
                  icon === ic ? 'bg-accent/20 text-accent' : 'bg-surface-raised text-text-muted hover:text-text hover:bg-surface'
                )}
              >
                {ic}
              </button>
            ))}
          </div>
        </div>

        {/* Privacy */}
        <div>
          <label className="text-xs text-text-muted mb-2 block">Privacy</label>
          <div className="flex gap-2">
            {['private', 'public'].map((p) => (
              <button
                key={p}
                onClick={() => setPrivacy(p)}
                className={cn(
                  'flex-1 h-8 rounded-lg text-sm capitalize transition-all active:scale-[0.97]',
                  privacy === p
                    ? 'bg-accent/10 text-accent border border-accent/30'
                    : 'bg-surface-raised text-text-muted border border-border hover:text-text'
                )}
              >
                {p}
              </button>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-1">
          <Button variant="ghost" onClick={onClose} className="flex-1">Cancel</Button>
          <Button variant="primary" onClick={handleCreate} disabled={!name.trim()} className="flex-1">
            Create project
          </Button>
        </div>
      </div>
    </Modal>
  )
}
